import { useState, useEffect, useRef, useCallback } from 'react';
import useOperationalStore, { getTimeHealth, isIdle, PIPELINE } from '../store/useOperationalStore';

const REFRESH_MS  = 5 * 60 * 1000; // auto-refresh every 5 minutes
const MODEL       = 'claude-haiku-4-5-20251001';

function buildPrompt(trucks, health) {
  const active    = trucks.filter(t => !['completed', 'voided'].includes(t.status));
  const hardDown  = active.filter(t => t.isHardDown);
  const overdue   = active.filter(t => getTimeHealth(t)?.status === 'red');
  const idle      = active.filter(isIdle);
  const approval  = active.filter(t => t.status === 'approval');
  const parts     = active.filter(t => t.status === 'parts-hold');

  const bottleneck = PIPELINE
    .map(s => ({ label: s.label, count: active.filter(t => s.statuses.includes(t.status)).length }))
    .filter(s => s.count > 0)
    .sort((a, b) => b.count - a.count)[0];

  const hdNames = hardDown.map(t => t.companyName || t.repairOrder).slice(0, 3).join(', ');

  return `You are an operations intelligence assistant for REKON. Write ONE sentence (max 18 words) summarizing live service operations right now. Be direct. Use operations language. No filler words.

Live data:
- Health score: ${health}/100
- Active Jobs: ${active.length}
- Hard down: ${hardDown.length}${hdNames ? ` (${hdNames})` : ''}
- Overdue: ${overdue.length}
- Idle jobs: ${idle.length}
- Awaiting approval: ${approval.length}
- Parts hold: ${parts.length}
- Busiest stage: ${bottleneck ? `${bottleneck.label} (${bottleneck.count})` : 'none'}

Reply with only the sentence.`;
}

export default function useShopSummary() {
  const trucks       = useOperationalStore(s => s.trucks);
  const getShopHealth = useOperationalStore(s => s.getShopHealth);

  const [summary, setSummary]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const lastFetchRef            = useRef(0);
  const lastSnapshotRef         = useRef('');
  const timerRef                = useRef(null);

  const fetch_ = useCallback(async () => {
    const health = getShopHealth();
    const active = trucks.filter(t => !['completed', 'voided'].includes(t.status));

    // Build a change key so we skip fetches when nothing meaningful changed
    const snapshot = `${active.length}|${active.filter(t => t.isHardDown).length}|${health}|${active.filter(t => getTimeHealth(t)?.status === 'red').length}`;
    const now = Date.now();

    if (snapshot === lastSnapshotRef.current && now - lastFetchRef.current < REFRESH_MS) return;
    lastSnapshotRef.current = snapshot;
    lastFetchRef.current    = now;

    if (!active.length) { setSummary('No active jobs in the system.'); return; }

    setLoading(true);
    setError(false);
    try {
      const res = await fetch('/api/anthropic', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model:      MODEL,
          max_tokens: 80,
          messages:   [{ role: 'user', content: buildPrompt(trucks, health) }],
        }),
      });
      const text_body = await res.text();
      const data = text_body ? JSON.parse(text_body) : {};
      if (!res.ok) {
        console.error('Anthropic API error:', res.status, data);
        const message = typeof data?.error === 'string' ? data.error : data?.error?.message || '';
        if (res.status === 503 && message === 'Anthropic API key is not configured') {
          setError('no-key');
        } else if (message.includes('credit balance')) {
          setError('no-credits');
        } else {
          setError('fetch-failed');
        }
      } else {
        const text = data?.content?.[0]?.text?.trim();
        if (text) setSummary(text);
        else setError('fetch-failed');
      }
    } catch (e) {
      console.error('Shop summary error:', e);
      setError('fetch-failed');
    }
    setLoading(false);
  }, [trucks, getShopHealth]);

  // Fetch on mount + whenever trucks change (debounced by snapshot key)
  useEffect(() => {
    fetch_();
  }, [fetch_]);

  // Also auto-refresh on a timer in case trucks haven't changed
  useEffect(() => {
    timerRef.current = setInterval(fetch_, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetch_]);

  return { summary, loading, error, refresh: fetch_ };
}
